import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Search, SlidersHorizontal, X, ChevronDown, ArrowLeft, Grid3X3, LayoutGrid } from "lucide-react";
import { products, categories } from "@/data/products";
import { SEOHead } from "@/components/SEOHead";
import { itemListSchema, breadcrumbSchema } from "@/data/schemas";

const sortOptions = [
  { id: "default", label: "Featured" },
  { id: "name-asc", label: "A to Z" },
  { id: "name-desc", label: "Z to A" },
  { id: "category", label: "By Category" },
];

export default function ShopPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCat = searchParams.get("category") || "all";
  const [activeCategory, setActiveCategory] = useState(initialCat);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("default");
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showMobileFilter, setShowMobileFilter] = useState(false);
  const [gridCols, setGridCols] = useState(3);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleCategoryChange = (catId) => {
    setActiveCategory(catId);
    if (catId === "all") {
      searchParams.delete("category");
    } else {
      searchParams.set("category", catId);
    }
    setSearchParams(searchParams);
  };

  let filtered = activeCategory === "all"
    ? products
    : products.filter((p) => p.category === activeCategory);

  if (searchQuery.trim()) {
    const q = searchQuery.toLowerCase();
    filtered = filtered.filter(
      (p) => p.name.toLowerCase().includes(q) || p.description.toLowerCase().includes(q) || p.categoryLabel.toLowerCase().includes(q)
    );
  }

  if (sortBy === "name-asc") filtered = [...filtered].sort((a, b) => a.name.localeCompare(b.name));
  if (sortBy === "name-desc") filtered = [...filtered].sort((a, b) => b.name.localeCompare(a.name));
  if (sortBy === "category") filtered = [...filtered].sort((a, b) => a.category.localeCompare(b.category));

  const activeCatLabel = categories.find((c) => c.id === activeCategory)?.label || "All Products";

  return (
    <div className="min-h-screen bg-[#FAFAF7]" data-testid="shop-page">
      <SEOHead
        title="Shop Eagle Diaries 2026 - Premium Diaries, Planners & Notebooks"
        description="Browse 17+ premium Eagle Diaries for 2026. Perfect Diary, Project Diary, Wings, Span, Club, Embassy and custom notebooks. Available for bulk corporate orders with gold foiling and customisation."
        path="/shop"
        schema={[itemListSchema(products), breadcrumbSchema([{ name: "Home", url: "/" }, { name: "Shop" }])]}
      />
      {/* Shop Header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAFAF7]/95 border-b border-black/5">
        <div className="container mx-auto px-6 sm:px-8 max-w-7xl">
          {/* Top Bar */}
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link
              to="/"
              data-testid="shop-back-home"
              className="flex items-center gap-3 text-[#1A1A1A] hover:text-[#C8A23A] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <img src="/eagle_logo_cropped.png" alt="Emcee Sons" className="h-10 sm:h-12 w-auto" />
            </Link>

            <h1
              className="hidden sm:block text-xl tracking-tight text-[#1A1A1A]"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
            >
              Shop
            </h1>

            <div className="flex items-center gap-3">
              <a
                href="tel:+919343716059"
                data-testid="shop-call-btn"
                className="hidden sm:flex items-center gap-2 bg-[#7A1A31] text-white px-5 py-2.5 rounded-full hover:bg-[#5C162E] transition-colors text-sm font-medium"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Call for Bulk Orders
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-[#7A1A31] via-[#8B1F38] to-[#7A1A31] py-12 sm:py-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-4 left-10 w-32 h-32 border border-white/20 rounded-full" />
          <div className="absolute bottom-4 right-20 w-20 h-20 border border-white/20 rounded-full" />
          <div className="absolute top-1/2 left-1/2 w-48 h-48 border border-white/10 rounded-full -translate-x-1/2 -translate-y-1/2" />
        </div>
        <div className="container mx-auto px-6 sm:px-8 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span
              className="text-xs tracking-[0.25em] uppercase text-[#C8A23A] font-semibold mb-3 block"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              Eagle Diaries Collection 2026
            </span>
            <h2
              className="text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700 }}
              data-testid="shop-page-heading"
            >
              Premium Diaries & Journals
            </h2>
            <p
              className="text-base text-white/60 max-w-lg"
              style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
            >
              Discover our complete range of handcrafted diaries, planners, and notebooks.
              Available for customisation and bulk corporate orders.
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-6 sm:px-8 max-w-7xl py-10">
        {/* Toolbar */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          {/* Search */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B7280]/50" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="shop-search"
              className="w-full pl-11 pr-4 py-3 bg-white border border-black/5 rounded-xl text-sm text-[#1A1A1A] placeholder-[#A0ABBA]/40 focus:outline-none focus:border-[#D4AF37]/50 transition-colors"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            />
            {searchQuery && (
              <button onClick={() => setSearchQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#6B7280]/40 hover:text-[#7A1A31]">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            {/* Grid toggle */}
            <div className="hidden lg:flex items-center gap-1 bg-white border border-black/5 rounded-xl p-1">
              <button
                onClick={() => setGridCols(3)}
                data-testid="grid-3"
                className={`p-2 rounded-xl transition-colors ${gridCols === 3 ? "bg-[#7A1A31] text-white" : "text-[#6B7280]/50 hover:text-[#7A1A31]"}`}
              >
                <Grid3X3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setGridCols(4)}
                data-testid="grid-4"
                className={`p-2 rounded-xl transition-colors ${gridCols === 4 ? "bg-[#7A1A31] text-white" : "text-[#6B7280]/50 hover:text-[#7A1A31]"}`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
            </div>

            {/* Sort */}
            <div className="relative">
              <button
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                data-testid="shop-sort-btn"
                className="flex items-center gap-2 px-4 py-3 bg-white border border-black/5 rounded-xl text-sm text-[#6B7280] hover:border-black/10 transition-colors"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Sort: {sortOptions.find((s) => s.id === sortBy)?.label}
                <ChevronDown className="w-4 h-4" />
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-2 bg-white border border-black/5 rounded-xl shadow-2xl z-20 min-w-[160px]">
                  {sortOptions.map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => { setSortBy(opt.id); setShowSortDropdown(false); }}
                      data-testid={`sort-${opt.id}`}
                      className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${sortBy === opt.id ? "text-[#C8A23A] bg-black/5" : "text-[#6B7280] hover:text-[#7A1A31] hover:bg-black/5"}`}
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowMobileFilter(!showMobileFilter)}
              data-testid="mobile-filter-btn"
              className="lg:hidden flex items-center gap-2 px-4 py-3 bg-white border border-black/5 rounded-xl text-sm text-[#6B7280]"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              <SlidersHorizontal className="w-4 h-4" />
              Filter
            </button>
          </div>
        </div>

        <div className="flex gap-8">
          {/* Sidebar Filters - Desktop */}
          <aside className="hidden lg:block w-56 flex-shrink-0" data-testid="shop-sidebar">
            <div className="sticky top-28">
              <h3
                className="text-xs tracking-[0.15em] uppercase text-[#C8A23A] font-semibold mb-5"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                Categories
              </h3>
              <div className="space-y-1">
                {categories.map((cat) => {
                  const count = cat.id === "all" ? products.length : products.filter((p) => p.category === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => handleCategoryChange(cat.id)}
                      data-testid={`sidebar-cat-${cat.id}`}
                      className={`w-full flex items-center justify-between px-4 py-2.5 rounded-xl text-sm transition-all ${
                        activeCategory === cat.id
                          ? "bg-[#7A1A31]/20 text-[#1A1A1A] border-l-2 border-[#D4AF37]"
                          : "text-[#6B7280] hover:text-[#7A1A31] hover:bg-black/5"
                      }`}
                      style={{ fontFamily: "'Outfit', sans-serif" }}
                    >
                      <span>{cat.label}</span>
                      <span className="text-xs text-[#6B7280]/40">{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Customisation CTA */}
              <div className="mt-10 p-5 bg-gradient-to-b from-[#7A1A31]/20 to-transparent border border-[#7A1A31]/20 rounded-xl">
                <h4
                  className="text-sm text-[#1A1A1A] mb-2"
                  style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
                >
                  Need Custom Diaries?
                </h4>
                <p
                  className="text-xs text-[#6B7280] mb-4 leading-relaxed"
                  style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 300 }}
                >
                  Gold foiling, page insertions, and fully bespoke designs for 2000+ units.
                </p>
                <a
                  href={`https://wa.me/919343716059?text=${encodeURIComponent("Hi! I'd like to discuss custom diary options for bulk orders.")}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="sidebar-custom-cta"
                  className="block text-center text-xs bg-[#7A1A31] text-white px-4 py-2.5 rounded-xl hover:bg-[#5C162E] transition-colors font-medium"
                  style={{ fontFamily: "'Outfit', sans-serif" }}
                >
                  Enquire Now
                </a>
              </div>
            </div>
          </aside>

          {/* Mobile Filters */}
          <AnimatePresence>
            {showMobileFilter && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="lg:hidden fixed inset-0 z-50 bg-[#FAFAF7]/95 backdrop-blur-xl overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-8">
                    <h3 className="text-lg text-[#1A1A1A]" style={{ fontFamily: "'Playfair Display', serif" }}>Filter</h3>
                    <button onClick={() => setShowMobileFilter(false)} className="text-[#1A1A1A]"><X className="w-6 h-6" /></button>
                  </div>
                  <div className="space-y-2">
                    {categories.map((cat) => {
                      const count = cat.id === "all" ? products.length : products.filter((p) => p.category === cat.id).length;
                      return (
                        <button
                          key={cat.id}
                          onClick={() => { handleCategoryChange(cat.id); setShowMobileFilter(false); }}
                          className={`w-full flex items-center justify-between px-5 py-4 rounded-xl text-base transition-all ${
                            activeCategory === cat.id ? "bg-[#7A1A31] text-white" : "text-[#6B7280] bg-white"
                          }`}
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          <span>{cat.label}</span>
                          <span className="text-sm opacity-50">{count}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Product Grid */}
          <div className="flex-1">
            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-sm text-[#6B7280]/60" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {activeCatLabel} — {filtered.length} {filtered.length === 1 ? "product" : "products"}
              </p>
            </div>

            <div className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols === 4 ? "lg:grid-cols-4" : "lg:grid-cols-3"} gap-5`}>
              <AnimatePresence mode="popLayout">
                {filtered.map((product, i) => (
                  <motion.div
                    key={product.id}
                    layout
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.35, delay: i * 0.03 }}
                  >
                    <Link
                      to={`/shop/${product.id}`}
                      data-testid={`shop-card-${product.id}`}
                      className="group block bg-white border border-black/5 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 hover:-translate-y-1 transition-all duration-400"
                    >
                      {/* Image */}
                      <div className="relative aspect-square bg-[#F5F5F3] overflow-hidden">
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          loading="lazy"
                        />
                        <div className="absolute top-3 left-3">
                          <span
                            className="text-[10px] tracking-[0.1em] uppercase bg-[#D4AF37]/90 text-[#0B1021] px-2.5 py-1 rounded-xl font-semibold"
                            style={{ fontFamily: "'Outfit', sans-serif" }}
                          >
                            {product.year}
                          </span>
                        </div>
                        <div className="absolute inset-0 bg-[#7A1A31]/0 group-hover:bg-[#7A1A31]/10 transition-colors duration-300" />
                      </div>

                      {/* Info */}
                      <div className="p-5">
                        <span
                          className="text-[10px] tracking-[0.1em] uppercase text-[#C8A23A]/70 font-medium block mb-1.5"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          {product.categoryLabel}
                        </span>
                        <h3
                          className="text-sm sm:text-base text-[#1A1A1A] mb-1.5 leading-snug"
                          style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600 }}
                        >
                          {product.name}
                        </h3>
                        <span
                          className="text-xs text-[#6B7280]/50 block mb-3"
                          style={{ fontFamily: "'Outfit', sans-serif" }}
                        >
                          {product.size}
                        </span>
                        <span
                          className="text-xs text-[#C8A23A] group-hover:text-[#7A1A31] transition-colors inline-flex items-center gap-1"
                          style={{ fontFamily: "'Outfit', sans-serif", fontWeight: 500 }}
                        >
                          View Details
                          <ChevronDown className="w-3 h-3 -rotate-90" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <p className="text-[#6B7280] text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>No products found</p>
                <p className="text-[#6B7280]/50 text-sm" style={{ fontFamily: "'Outfit', sans-serif" }}>Try adjusting your search or filter.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="border-t border-black/5 py-8 mt-10">
        <div className="container mx-auto px-6 sm:px-8 max-w-7xl flex flex-col sm:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/eagle_logo_cropped.png" alt="Emcee Sons" className="h-8 w-auto" />
          </Link>
          <p className="text-xs text-[#6B7280]/40" style={{ fontFamily: "'Outfit', sans-serif" }}>
            &copy; {new Date().getFullYear()} Eagle Diaries — Emcee Sons Diary House
          </p>
        </div>
      </div>
    </div>
  );
}
